import {
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Text,
    HStack,
    Flex,
    Box,
    Alert,
    AlertIcon,
    AlertDescription,
    useToast,
} from '@chakra-ui/react';
import PartBayButton from '../components/PartBayButton';
import ResetToPartBaySelectionsIcon from '../components/ResetToPartBaySelectionsIcon';
import { useResetToPartbaySelections } from '../hooks/useResetToPartbaySelections';
import { brandCopy } from '../constants/brand';

interface ResetSelectionsConfirmationModalProps {
    isOpen: boolean;
    onClose: () => void;
    orderId: string;
}

const ResetSelectionsConfirmationModal = ({
    isOpen,
    onClose,
    orderId,
}: ResetSelectionsConfirmationModalProps) => {
    const resetSelections = useResetToPartbaySelections();
    const toast = useToast();

    const handleConfirm = async () => {
        try {
            const result = await resetSelections.mutateAsync({ orderId });
            onClose();
            toast({
                title: result.partsReset > 0
                    ? brandCopy(`Reset ${result.partsReset} ${result.partsReset === 1 ? 'part' : 'parts'} to PartBay selections`)
                    : brandCopy('Selections already match PartBay selections'),
                status: 'success',
                duration: 5000,
                isClosable: true,
            });
        } catch (error: any) {
            toast({
                title: error?.response?.data?.message || 'Failed to reset selections',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
            <ModalOverlay />
            <ModalContent borderRadius="xl">
                <ModalHeader
                    bg="gray.50"
                    borderTopRadius="xl"
                    borderBottomWidth="1px"
                    borderColor="gray.300"
                    color="gray.800"
                    fontSize="lg"
                    fontWeight="semibold"
                >
                    <HStack spacing={2.5}>
                        <Flex
                            w="32px"
                            h="32px"
                            borderRadius="full"
                            bg="#E6F1FD"
                            align="center"
                            justify="center"
                            flexShrink={0}
                        >
                            <ResetToPartBaySelectionsIcon size={18} color="#0071E3" />
                        </Flex>
                        <Text>{brandCopy('Reset to PartBay Selections')}</Text>
                    </HStack>
                </ModalHeader>
                <ModalCloseButton />

                <ModalBody py={5}>
                    <Text fontSize="sm" color="gray.700" lineHeight="1.6">
                        {brandCopy('Are you sure you want to change your selections back to the original selections made by PartBay?')}
                    </Text>
                    <Box mt={4}>
                        <Alert
                            status="warning"
                            borderRadius="md"
                            border="1px solid"
                            borderColor="orange.200"
                            bg="orange.50"
                        >
                            <AlertIcon color="orange.500" />
                            <AlertDescription fontSize="sm" color="orange.800">
                                This is irreversible.
                            </AlertDescription>
                        </Alert>
                    </Box>
                </ModalBody>

                <ModalFooter justifyContent="space-between">
                    <Button
                        size="action"
                        variant="outlineGray"
                        onClick={onClose}
                        isDisabled={resetSelections.isPending}
                    >
                        Cancel
                    </Button>
                    <PartBayButton
                        size="action"
                        colorScheme="partbay-blue"
                        variant="solid"
                        text="Reset Selections"
                        loadingText="Resetting..."
                        onClick={handleConfirm}
                        isLoading={resetSelections.isPending}
                    />
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default ResetSelectionsConfirmationModal;
